import React from 'react';
import { Grid } from '@material-ui/core';
import { Formik, Form } from 'formik';
import { addDays } from 'date-fns';
import { zonedTimeToUtc, utcToZonedTime } from 'date-fns-tz';
import { stateToTzMap } from '@ourbranch/state-to-tz-map';
import * as Yup from 'yup';

import withDatePicker from 'core/components/with-date-picker';
import { Card } from 'core/components/card';
import { Button } from 'core/components/button';
import { FormField } from 'core/components/form';
import { awsDateTimeFormatter } from 'core/helpers/formatters';
import { useToast } from 'core/components/toast';
import useSession from 'core/hooks/use-session';
import { useStore } from 'core/store';
import useStyles from './settings.styles';

const validationSchema = Yup.object().shape({
  skipUnderwritingUntil: Yup.string()
    .nullable()
    .test('min-date-today', 'Resume date must be in the future', (value) => {
      if (!value) {
        return true; // Allow null/empty values
      }
      try {
        const resumeDate = new Date(value);
        const today = new Date();
        today.setUTCHours(0, 0, 0, 0); // Start of today
        return resumeDate >= today;
      } catch (error) {
        return false; // Invalid date format
      }
    })
    .test('max-date-seven-days', 'Resume date cannot be more than 7 days from today', (value) => {
      if (!value) {
        return true; // Allow null/empty values
      }
      try {
        const resumeDate = new Date(value);
        const maxDate = addDays(new Date(), 7);
        maxDate.setUTCHours(23, 59, 59, 999); // End of the 7th day
        return resumeDate <= maxDate;
      } catch (error) {
        return false; // Invalid date format
      }
    })
});

const OfferConfiguration = () => {
  const classes = useStyles();
  const { canSkipOfferUnderwritingRules } = useSession();
  const { offer: offerStore } = useStore();
  const toast = useToast();

  if (!canSkipOfferUnderwritingRules) {
    return null;
  }

  // Get current value from offer store and policy state
  const currentSkipUntilValue = offerStore.offer?.skipUnderwritingUntil;
  const policyState = offerStore.offer?.quote?.correctedAddress?.state;
  const timezone = policyState ? stateToTzMap[policyState] : 'America/New_York'; // Default to ET if no state

  // Convert UTC stored value to local timezone for display
  const getDisplayValue = (utcValue) => {
    if (!utcValue) {
      return null;
    }
    try {
      // Convert UTC stored value to policy state timezone for display
      const utcDate = new Date(utcValue);
      const localDate = utcToZonedTime(utcDate, timezone);
      return localDate.toISOString().split('T')[0]; // Return just the date part for the date picker
    } catch (error) {
      return null;
    }
  };

  // Convert local date selection to UTC for storage
  const convertToUTC = (localDateString) => {
    if (!localDateString) {
      return null;
    }
    try {
      // Create date at 12AM in the policy's timezone
      const localDate = new Date(localDateString + 'T00:00:00');
      // Convert to UTC
      const utcDate = zonedTimeToUtc(localDate, timezone);
      return awsDateTimeFormatter(utcDate);
    } catch (error) {
      return null;
    }
  };

  // Generate dynamic helper text based on current value
  const getHelperText = (value) => {
    if (!value) {
      return `Select the date when underwriting rules should resume. Once a date is selected, underwriting rules will resume at 12AM ${policyState || 'local'} time on that day.`;
    }

    try {
      const skipUntilDate = new Date(value);
      const localDate = utcToZonedTime(skipUntilDate, timezone);
      const formattedDate = localDate.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

      return `Underwriting rules will be skipped until ${formattedDate} at 12:00 AM ${policyState || 'local'} time`;
    } catch (error) {
      return 'Select the date when underwriting rules should resume';
    }
  };

  const handleSubmit = async (values, { setSubmitting, setFieldError, resetForm }) => {
    try {
      // Convert the local date selection to UTC for storage
      const utcValue = convertToUTC(values.skipUnderwritingUntil);
      const result = await offerStore.updateOfferAdvancedConfiguration(utcValue);
      if (result.success) {
        toast.notify({
          type: 'success',
          message: 'Advanced configuration updated successfully'
        });
        // Reset form to mark it as clean with the new values
        resetForm({ values });
      } else {
        setFieldError('skipUnderwritingUntil', result.message);
        toast.notify({
          type: 'error',
          message: result.message
        });
      }
    } catch (error) {
      const errorMessage = 'Failed to update advanced configuration';
      setFieldError('skipUnderwritingUntil', errorMessage);
      toast.notify({
        type: 'error',
        message: errorMessage
      });
    } finally {
      setSubmitting(false);
    }
  };

  const initialValues = {
    skipUnderwritingUntil: getDisplayValue(currentSkipUntilValue)
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize={true}
    >
      {({ setFieldValue, isSubmitting, dirty, isValid, values }) => (
        <>
          <Form>
            <Card type="secondary" className={classes.advancedContainer}>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <FormField
                    name="skipUnderwritingUntil"
                    id="skipUnderwritingUntil"
                    type="date"
                    label="Resume Underwriting Rules On"
                    placeholder="Resume Underwriting Rules On"
                    mode="dark"
                    disableFuture={false}
                    disablePast={true}
                    minDate={addDays(new Date(), 1)} // Tomorrow
                    maxDate={addDays(new Date(), 7)} // 7 days from today
                    helperText={getHelperText(
                      values.skipUnderwritingUntil ? convertToUTC(values.skipUnderwritingUntil) : currentSkipUntilValue
                    )}
                    disabled={isSubmitting}
                    defaultToEmpty={true}
                  />
                </Grid>
                <Grid item xs={4} className={classes.button}>
                  <Button
                    color="secondary"
                    onClick={() => setFieldValue('skipUnderwritingUntil', null)}
                    disabled={!values.skipUnderwritingUntil || isSubmitting}
                  >
                    Clear
                  </Button>
                </Grid>
              </Grid>
            </Card>
            <Grid container justifyContent="flex-end" className={classes.button}>
              <Button type="submit" variant="contained" color="primary" disabled={!dirty || !isValid || isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save'}
              </Button>
            </Grid>
          </Form>
        </>
      )}
    </Formik>
  );
};

export default withDatePicker(OfferConfiguration);
